import React, { useState } from 'react';
import './Contact.css';

const PRACTICE_TYPES = ['Primary Care', 'Specialty Practice', 'Multi-Specialty Group', 'Health System', 'Other'];
const PROVIDER_COUNTS = ['1-5', '6-10', '11-25', '26-50', '50+'];
const SERVICE_OPTIONS = [
  ' Chronic Care Management (CCM)',
  ' Principal Care Management (PCM)',
  ' Remote Patient Monitoring (RPM)',
  ' Transitional Care Management (TCM)',
  ' Principal Illness Navigation (PIN)'
];
const CARE_MANAGEMENT_STATUS_OPTIONS = [
  ' Not currently implemented',
  ' Managed internally',
  ' Managed by external partner',
  ' Evaluating options'
];
const PATIENT_VOLUME_OPTIONS = ['Under 50', '50-100', '101-250', '250+', 'Not sure'];
const CONTACT_METHOD_OPTIONS = [' Email', ' Phone', ' Video call'];
const CONTACT_TIME_OPTIONS = ['Morning', 'Afternoon', 'Flexible'];

const FORMSPREE_FORM_ID = process.env.REACT_APP_FORMSPREE_FORM_ID;

const Contact = () => {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [formData, setFormData] = useState({
    practiceName: '',
    practiceType: '',
    specialty: '',
    providerCount: '',
    ehrType: '',
    contactName: '',
    titleRole: '',
    email: '',
    phone: '',
    services: [],
    goals: '',
    careManagementStatus: '',
    patientVolume: '',
    preferredContact: '',
    bestTimeToContact: '',
    additionalInfo: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleCheckboxChange = (service) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(service)
        ? prev.services.filter(s => s !== service)
        : [...prev.services, service]
    }));
  };

  const resetForm = () => {
    setFormData({
      practiceName: '',
      practiceType: '',
      specialty: '',
      providerCount: '',
      ehrType: '',
      contactName: '',
      titleRole: '',
      email: '',
      phone: '',
      services: [],
      goals: '',
      careManagementStatus: '',
      patientVolume: '',
      preferredContact: '',
      bestTimeToContact: '',
      additionalInfo: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.services.length === 0) {
      setSubmitError('Please select at least one program you are exploring.');
      return;
    }
    setSubmitError(null);
    if (!FORMSPREE_FORM_ID) {
      setSubmitError('Form is not configured. Please set REACT_APP_FORMSPREE_FORM_ID.');
      return;
    }
    setSubmitError(null);
    setSubmitting(true);
    try {
      const payload = {
        _replyto: formData.email,
        _subject: `Solace Care 360 – Practice inquiry from ${formData.practiceName || 'Contact form'}`,
        practiceName: formData.practiceName,
        practiceType: formData.practiceType,
        specialty: formData.specialty,
        providerCount: formData.providerCount,
        ehrType: formData.ehrType,
        contactName: formData.contactName,
        titleRole: formData.titleRole,
        email: formData.email,
        phone: formData.phone,
        servicesOfInterest: formData.services.join('; '),
        goals: formData.goals,
        careManagementStatus: formData.careManagementStatus,
        patientVolume: formData.patientVolume,
        preferredContact: formData.preferredContact,
        bestTimeToContact: formData.bestTimeToContact,
        additionalInfo: formData.additionalInfo
      };
      const res = await fetch(`https://formspree.io/f/${FORMSPREE_FORM_ID}`, {
        method: 'POST',
        body: JSON.stringify(payload),
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      await res.json().catch(() => ({}));
      if (!res.ok) throw new Error('Submission failed');
      setSubmitted(true);
      resetForm();
    } catch (err) {
      setSubmitError('Something went wrong. Please try again or email contact@solacecare360.com directly.');
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 0);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <section className="contact">
        <div className="container">
          <div className="contact-success">
            <h2>Thank You</h2>
            <p>Your request has been submitted. Our team will be in touch soon.</p>
            <button className="btn btn-primary" onClick={() => setSubmitted(false)}>Submit Another Request</button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="contact">
      <div className="container">
        <h2 className="contact-title">Request a Consultation</h2>
        <p className="contact-intro">
          Complete the form below and a member of our nursing leadership team will contact you to discuss your organization's goals, current workflow, and opportunities for care management alignment.
        </p>
        {submitError && (
          <p className="contact-error" role="alert">{submitError}</p>
        )}
        <form className="contact-form" onSubmit={handleSubmit}>
          <div className="form-section">
            <h3 className="form-section-title">Practice Information</h3>
            <div className="form-group">
              <label>Practice / Organization Name *</label>
              <input type="text" name="practiceName" value={formData.practiceName} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>Practice Type *</label>
              <select name="practiceType" value={formData.practiceType} onChange={handleChange} required>
                <option value="">Select...</option>
                {PRACTICE_TYPES.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Specialty (if applicable)</label>
              <input type="text" name="specialty" value={formData.specialty} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Number of Providers *</label>
              <select name="providerCount" value={formData.providerCount} onChange={handleChange} required>
                <option value="">Select...</option>
                {PROVIDER_COUNTS.map(count => (
                  <option key={count} value={count}>{count}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>EHR type *</label>
              <input type="text" name="ehrType" value={formData.ehrType} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>Primary Contact Name *</label>
              <input type="text" name="contactName" value={formData.contactName} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>Title / Role *</label>
              <input type="text" name="titleRole" value={formData.titleRole} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>Email Address *</label>
              <input type="email" name="email" value={formData.email} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>Phone Number *</label>
              <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required />
            </div>
          </div>
          <div className="form-section">
            <h3 className="form-section-title">Programs You Are Exploring</h3>
            <div className="form-group">
              <label>Which programs are you exploring? (Select all that apply) *</label>
              <div className="form-checkboxes">
                {SERVICE_OPTIONS.map(service => (
                  <label key={service} className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.services.includes(service)}
                      onChange={() => handleCheckboxChange(service)}
                    />
                    <span>{service}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="form-section">
            <h3 className="form-section-title">Practice Needs &amp; Goals</h3>
            <div className="form-group">
              <label>What strategic goals are you aiming to address through care management? *</label>
              <p className="form-helper">e.g. Improving patient engagement, Expanding care management capacity, Supporting quality initiatives, Reducing staff burden</p>
              <textarea name="goals" rows="4" value={formData.goals} onChange={handleChange} placeholder="Tell us about your goals..." required />
            </div>
            <div className="form-group">
              <label>Current Care Management Status *</label>
              <div className="form-radios">
                {CARE_MANAGEMENT_STATUS_OPTIONS.map(option => (
                  <label key={option} className="radio-label">
                    <input
                      type="radio"
                      name="careManagementStatus"
                      value={option}
                      checked={formData.careManagementStatus === option}
                      onChange={handleChange}
                      required
                    />
                    <span>{option}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="form-group">
              <label>Approximate Patient Volume for Care Management (if known) *</label>
              <select name="patientVolume" value={formData.patientVolume} onChange={handleChange} required>
                <option value="">Select...</option>
                {PATIENT_VOLUME_OPTIONS.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="form-section">
            <h3 className="form-section-title">Consultation Preferences</h3>
            <div className="form-group">
              <label>Preferred Method of Contact *</label>
              <div className="form-radios">
                {CONTACT_METHOD_OPTIONS.map(option => (
                  <label key={option} className="radio-label">
                    <input
                      type="radio"
                      name="preferredContact"
                      value={option}
                      checked={formData.preferredContact === option}
                      onChange={handleChange}
                      required
                    />
                    <span>{option}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="form-group">
              <label>Best Time to Contact You *</label>
              <select name="bestTimeToContact" value={formData.bestTimeToContact} onChange={handleChange} required>
                <option value="">Select...</option>
                {CONTACT_TIME_OPTIONS.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="form-section">
            <h3 className="form-section-title">Additional Information</h3>
            <div className="form-group">
              <label>Anything else you&apos;d like us to know?</label>
              <textarea name="additionalInfo" rows="3" value={formData.additionalInfo} onChange={handleChange} placeholder="Optional" />
            </div>
          </div>
          <div className="form-actions">
            <p className="contact-privacy-note">We respect your time and privacy. All inquiries are handled confidentially by our nursing leadership team.</p>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? 'Sending...' : 'Submit Request'}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};

export default Contact;
